import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface VoiceSettingsContextType {
  voiceResponsesEnabled: boolean;
  setVoiceResponsesEnabled: (enabled: boolean) => void;
  toggleVoiceResponses: () => void;
}

const VoiceSettingsContext = createContext<VoiceSettingsContextType | undefined>(undefined);

export function VoiceSettingsProvider({ children }: { children: ReactNode }) {
  const [voiceResponsesEnabled, setVoiceResponsesEnabledState] = useState(() => {
    const saved = localStorage.getItem("voiceResponsesEnabled");
    // Default to OFF when no value saved
    return saved !== null ? saved === "true" : false;
  });

  const setVoiceResponsesEnabled = useCallback((enabled: boolean) => {
    setVoiceResponsesEnabledState(enabled);
    localStorage.setItem("voiceResponsesEnabled", String(enabled));
  }, []);

  const toggleVoiceResponses = useCallback(() => {
    setVoiceResponsesEnabled(!voiceResponsesEnabled);
  }, [voiceResponsesEnabled, setVoiceResponsesEnabled]);

  return (
    <VoiceSettingsContext.Provider
      value={{
        voiceResponsesEnabled,
        setVoiceResponsesEnabled,
        toggleVoiceResponses,
      }}
    >
      {children}
    </VoiceSettingsContext.Provider>
  );
}

export function useVoiceSettings() {
  const context = useContext(VoiceSettingsContext);
  if (!context) {
    throw new Error("useVoiceSettings must be used within a VoiceSettingsProvider");
  }
  return context;
}
